/**
 * Hawker Load service
 */
import { Injectable, Injector } from '@angular/core';

import { BaseService } from '../base';
import {
  IEntityConfig,
  ENTITY_TYPES,
  IXVIListColumn,
  IBaseEntityType,
  CELL_RENDERER,
  GRID_ROW_SELECTION,
  ListColumn,
  ILimit,
  EPRODUCT_TYPES,
  EPlacement,
  IApiListResponse,
} from '@shared/types';
import { BehaviorSubject, Observable, rx } from '@app/shared/rx';
import { I18n } from '@app/i18n/strings-keys';
import {
  assign,
  chain,
  find,
  get,
  head,
  intersection,
  isEmpty,
  map,
  pick,
  set,
  uniq,
} from 'lodash';
import { FormControl } from '@angular/forms';
import {
  MajorReportingCategoryPath,
  MinorReportingCategoryPath,
} from '@app/shared/constants';
import { ITreeData } from '@app/shared/types/common/tree.type';
import { getSortedTreeDataByConfig } from '@app/shared/utils/common-utils';
import { XUXListContainerComponent } from '@app/containers/base/list/xux-list.container.component';
import { getVariesPlaceholderFunc } from '@app/shared/utils/placeholder-utils';
import {
  getListGridRowCellClass,
  shouldUpdateChildWhenNotEmpty,
} from '@app/shared/helpers/ag-grid-helpers';
import { getProductColumns } from '@app/shared/utils/product-columns.utils';
import { ComponentSelectorResult } from 'ag-grid-community/dist/lib/components/framework/userComponentFactory';
import { isLeaf } from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { DEFAULT_LOAD } from '@config/global-constants';

const entityType: ENTITY_TYPES = ENTITY_TYPES.HAWKER_LOAD;
const config: IEntityConfig = {
  ENTITY_TYPE: entityType,
  isMasterListAvailable: true,
  entityNameToCheckPermissions: ENTITY_TYPES.HAWKER_CONFIG,
  buttons: null,
  apiEndpoint: entityType,
  uiRoute: entityType,
  list: {
    userGridOptions: {
      enableServerSideFilter: false,
      enableServerSideSorting: false,
      rowSelection: GRID_ROW_SELECTION.SINGLE,
      suppressRowClickSelection: true,
      // key field for treeMode data shown in ag-grid
      deltaRowDataMode: false,
      rowClassRules: {
        ...getListGridRowCellClass(),
      },
    },
    gridOptions: {
      multiselect: false,
      treeViewMode: true,
      defaultExpanded: true,
      expandedMaxLevel: 1,
      treeChildrenPropertyName: 'children',
      syncTreeFieldAfterFilter: 'load',
      disableColumnSelector: true,
      showViewSelector: false,
      showSearch: true,
      showPagination: false,
      hiddenGridHeaderCheckbox: true,
      enableDynamicExpandBtn: true,
    },
    isHiddenRefreshButton: true,
    documentQuery: {
      notNeedActive: true,
    },
    keepAllDataOnSave: true,
    shouldUpdateNodes: shouldUpdateChildWhenNotEmpty,
    afterFetchListData: (items, options) => {
      const combinedData = combineXVIToDMProducts({
        gridItems: items,
        dependentData: options.dependentEntitiesData,
      });

      const listTreeItems = convertProductsToTree(combinedData);

      return listTreeItems;
    },
    dependentEntities: [
      {
        entityType: ENTITY_TYPES.PRODUCT,
        query: {
          filter: `product_type eq in(${[
            EPRODUCT_TYPES.STANDARD,
            EPRODUCT_TYPES.RETAIL_ITEM,
            EPRODUCT_TYPES.MODIFIER,
            EPRODUCT_TYPES.ENTITLEMENT,
            EPRODUCT_TYPES.GIFT_CARD,
          ].join(',')}) and is_active eq in(true)`,
          include_inactive: false,
          include_entities: [
            ENTITY_TYPES.PRODUCT,
            ENTITY_TYPES.TAG,
            ENTITY_TYPES.REPORTING_CATEGORY,
          ],
          select: [
            'name',
            'product_id',
            'tags.name',
            'reporting_category',
            MinorReportingCategoryPath,
            MajorReportingCategoryPath,
          ],
          split_site_documents: false,
        },
        options: {
          customizeFetchListMethod: 'fetchListByCommissarySites',
        },
      },
    ],
    titleTooltipIconConfig$: new BehaviorSubject({
      tooltipConfig: {
        tooltip: I18n.Forms.HawkerLoadForm.HawkerLoadTooltip,
      },
    }),
  },
};

/**
 * Hawker Load service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class HawkerLoadService extends BaseService {
  public static config: IEntityConfig = config;
  public static ENTITY_TYPE: string = config.ENTITY_TYPE;

  public quickFilterFields: string[] = ['name'];

  public listProjection: string[] = ['name'];

  public xuxListContainer: XUXListContainerComponent;
  public beforeStartListMethods: Function[] = [this.hookXuxListContainer];

  protected dependentData$: BehaviorSubject<Record<string, any>> =
    new BehaviorSubject<Record<string, any>>({});

  public constructor(public injector: Injector) {
    super(injector);
    this.config = HawkerLoadService.config;

    const hasManagePermission = this.permissionService.canManage(
      this.config.entityNameToCheckPermissions,
    );

    const loadLimit: ILimit<number> = {
      maximum: 999,
      minimum: 1,
    };

    set(this.config, 'list.columns', <IXVIListColumn[]>[
      ...getProductColumns({
        tagFilterValues: getTagsObs({ dependentData$: this.dependentData$ }),
      }),
      {
        path: 'load',
        title: I18n.Forms.HawkerLoadForm.Load,
        defaultView: true,
        minWidth: 100,
        maxWidth: 100,
        editable: hasManagePermission,
        headerComponentParams: {
          hintOptions: {
            description: this.i18nService.t(I18n.Common.Each),
            placement: EPlacement.top,
          },
        },
        cellRendererSelector: (params): ComponentSelectorResult | null => {
          const component = CELL_RENDERER.float_input_renderer;

          const renderParams: ListColumn['cellRendererParams'] = {
            defaultValue: loadLimit.minimum,
            label: null,
            precision: 0,
            max: loadLimit.maximum,
            min: loadLimit.minimum,
            usePreferenceDecimalPrecision: {
              overrideMin: false,
              overrideMax: false,
            },
            disabled: !hasManagePermission,
            class: 'input-text-left',
            blurWhenToNext: false,
            compareChangeWithValueBeforeEdit: true,
            disableKeyUpFinishEditing: true,
            allowEmptyValue: false,
            placeholderFunc: getVariesPlaceholderFunc({
              i18nService: this.i18nService,
            }),
          };

          return {
            component,
            params: isLeaf(params)
              ? renderParams
              : assign({}, renderParams, {
                  allowEmptyValue: true,
                }),
          };
        },
      },
    ]);
  }

  public hookXuxListContainer(
    xuxListContainerComponent: XUXListContainerComponent,
  ): void {
    this.xuxListContainer = xuxListContainerComponent;
  }

  public formListControlToGridMapper(
    control: FormControl,
    data: {
      gridItems: IBaseEntityType[];
      dependentData?: Record<string, IApiListResponse<IBaseEntityType>>;
    },
  ): IBaseEntityType[] {
    this.dependentData$.next(data.dependentData || {});

    return data?.gridItems;
  }
}

export const combineXVIToDMProducts = (data: {
  gridItems: IBaseEntityType[];
  dependentData?: Record<string, IApiListResponse<IBaseEntityType>>;
}) => {
  const { gridItems, dependentData } = data;

  if (isEmpty(gridItems)) {
    return [];
  }

  const xviProducts = head(gridItems)?.products || [];
  const xviProductIds = map(xviProducts, 'entity_id');

  const dmProducts = get(dependentData, [ENTITY_TYPES.PRODUCT, 'items'], []);
  const dmProductIds = map(dmProducts, 'entity_id');

  const intersectionProductIds = intersection(xviProductIds, dmProductIds);

  return map(intersectionProductIds, (productId) => {
    const targetXVIProduct = find(xviProducts, { entity_id: productId });
    const targetDMProduct = find(dmProducts, { entity_id: productId });

    return {
      entity_id: productId,
      ...pick(targetDMProduct, [
        'name',
        'reporting_category',
        'product_id',
        'tags',
      ]),
      load: get(targetXVIProduct, 'load', DEFAULT_LOAD),
    };
  });
};

export const convertProductsToTree = (
  items: IBaseEntityType[],
): ITreeData[] => {
  return getSortedTreeDataByConfig(items, {
    path: MajorReportingCategoryPath,
    groupName: 'name',
    additionalTreeFn: (name, baseTreeNode): IBaseEntityType => {
      const allLoads = map(get(baseTreeNode, 'children', []), 'load');

      const isSame = uniq(allLoads).length === 1;

      return {
        isMajorCategoryRow: true,
        load: isSame ? head(allLoads) : null,
        reporting_category_major: {
          name,
        },
      };
    },
    childrenConfig: {
      path: MinorReportingCategoryPath,
      additionalTreeFn: (name, baseTreeNode): IBaseEntityType => {
        const allLoads = map(get(baseTreeNode, 'children', []), 'load');

        const isSame = uniq(allLoads).length === 1;

        return {
          isMinorCategoryRow: true,
          load: isSame ? head(allLoads) : null,
          reporting_category_minor: {
            name,
          },
        };
      },
      groupName: 'name',
      childrenConfig: {
        path: 'name',
        secondarySortPath: 'product_id',
      },
    },
  });
};

export const getTagsObs = (options: {
  dependentData$: Observable<Record<string, any>>;
}) => {
  return options.dependentData$.pipe(
    rx.map((dependentData) => {
      return chain(get(dependentData, [ENTITY_TYPES.PRODUCT, 'items'], []))
        .flatMap('tags')
        .compact()
        .uniqBy('entity_id')
        .sortBy('name')
        .map((tag) => ({
          ...tag,
          text: tag.name,
          value: tag.entity_id,
        }))
        .value();
    }),
  );
};
